import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { ExportOutlined } from '@ant-design/icons';

import request from '@/request';

const CHANNEL_FEE_BASIS_POINTS = 54;

const parseAmountToCents = (value) => {
  const normalized = String(value ?? '').trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;

  const [yuan, decimal = ''] = normalized.split('.');
  const decimalWithPadding = `${decimal}000`;
  let cents = Number(yuan) * 100 + Number(decimalWithPadding.slice(0, 2));
  if (Number(decimalWithPadding[2]) >= 5) cents += 1;

  if (
    !Number.isSafeInteger(cents) ||
    cents > Number.MAX_SAFE_INTEGER / CHANNEL_FEE_BASIS_POINTS
  ) {
    return null;
  }
  return cents;
};

const formatAmount = (cents) => {
  const yuan = Math.floor(cents / 100);
  const decimal = String(cents % 100).padStart(2, '0');
  return `¥ ${yuan}.${decimal}`;
};

export default function Statement({ event }) {
  const [totalAmountCents, setTotalAmountCents] = useState(null);

  useEffect(() => {
    let active = true;
    setTotalAmountCents(null);

    if (event?.id) {
      request
        .get(`/statistics/pay/amount/total/${event.id}`)
        .then((result) => {
          if (active) setTotalAmountCents(parseAmountToCents(result));
        })
        .catch(() => {
          if (active) setTotalAmountCents(null);
        });
    }

    return () => {
      active = false;
    };
  }, [event?.id]);

  const hasStatementData = totalAmountCents !== null;
  const statementRows = useMemo(() => {
    if (!hasStatementData) {
      return [
        { label: '收入金额', amount: '-' },
        { label: '渠道手续费', rate: '0.54%', amount: '-' },
        { label: '平台手续费', rate: '0%', amount: '-' },
        { label: '结算金额', amount: '-' },
      ];
    }

    const channelFeeCents = Math.round(
      (totalAmountCents * CHANNEL_FEE_BASIS_POINTS) / 10000
    );
    const settlementAmountCents = totalAmountCents - channelFeeCents;

    return [
      { label: '收入金额', amount: formatAmount(totalAmountCents) },
      {
        label: '渠道手续费',
        rate: '0.54%',
        amount: formatAmount(channelFeeCents),
      },
      { label: '平台手续费', rate: '0%', amount: formatAmount(0) },
      { label: '结算金额', amount: formatAmount(settlementAmountCents) },
    ];
  }, [hasStatementData, totalAmountCents]);

  const exportStatement = useCallback(() => {
    if (!hasStatementData) {
      message.warning('没有对账单数据');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 3508;
    canvas.height = 2480;

    const context = canvas.getContext('2d');
    if (!context) {
      message.error('对账单导出失败');
      return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const title = `${event?.name || '赛事名称'} 对账单`;
    let titleSize = 104;
    context.font = `700 ${titleSize}px "Microsoft YaHei", sans-serif`;
    while (context.measureText(title).width > canvas.width - 480) {
      titleSize -= 4;
      context.font = `700 ${titleSize}px "Microsoft YaHei", sans-serif`;
    }
    context.fillStyle = '#1f2937';
    context.textAlign = 'center';
    context.fillText(title, canvas.width / 2, 300);

    const panelX = 260;
    const panelY = 470;
    const panelWidth = canvas.width - panelX * 2;
    const rowHeight = 280;
    const panelHeight = rowHeight * statementRows.length;

    context.strokeStyle = '#cbd5e1';
    context.lineWidth = 4;
    context.strokeRect(panelX, panelY, panelWidth, panelHeight);

    statementRows.forEach((row, index) => {
      const rowTop = panelY + index * rowHeight;
      const textY = rowTop + 178;

      if (index > 0) {
        context.beginPath();
        context.moveTo(panelX, rowTop);
        context.lineTo(panelX + panelWidth, rowTop);
        context.stroke();
      }

      context.fillStyle = '#334155';
      context.font = '600 68px "Microsoft YaHei", sans-serif';
      context.textAlign = 'left';
      context.fillText(row.label, panelX + 130, textY);

      if (row.rate) {
        context.font = '600 64px "Microsoft YaHei", sans-serif';
        context.textAlign = 'center';
        context.fillText(row.rate, canvas.width / 2, textY);
      }

      context.font = '700 72px "Microsoft YaHei", sans-serif';
      context.textAlign = 'right';
      context.fillText(row.amount, panelX + panelWidth - 130, textY);
    });

    context.fillStyle = '#1f2937';
    context.font = '600 66px "Microsoft YaHei", sans-serif';
    context.textAlign = 'left';
    const partyAX = panelX + 120;
    const partyBX = canvas.width / 2 + 120;
    context.fillText('甲方签名/盖章确认：', partyAX, 2040);
    context.fillText('日期：', partyAX, 2250);
    context.fillText('乙方签名/盖章确认：', partyBX, 2040);
    context.fillText('日期：', partyBX, 2250);

    context.strokeStyle = '#64748b';
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(partyAX + 700, 2060);
    context.lineTo(canvas.width / 2 - 180, 2060);
    context.moveTo(partyAX + 220, 2270);
    context.lineTo(canvas.width / 2 - 180, 2270);
    context.moveTo(partyBX + 700, 2060);
    context.lineTo(canvas.width - panelX - 120, 2060);
    context.moveTo(partyBX + 220, 2270);
    context.lineTo(canvas.width - panelX - 120, 2270);
    context.stroke();

    canvas.toBlob((blob) => {
      if (!blob) {
        message.error('对账单导出失败');
        return;
      }

      const fileUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = (event?.name || '赛事名称').replace(
        /[\\/:*?"<>|]/g,
        '_'
      );
      link.href = fileUrl;
      link.download = `${fileName}-对账单.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
      message.success('导出成功');
    }, 'image/png');
  }, [event?.name, hasStatementData, statementRows]);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-slate-200 bg-white p-8">
        <h1 className="text-left text-3xl font-semibold text-slate-800">
          {event?.name || '赛事名称'} 对账单
        </h1>

        <div className="mt-8 divide-y divide-slate-100">
          {statementRows.map((row) => (
            <div
              key={row.label}
              className="grid min-h-18 grid-cols-[1fr_1fr_1fr] items-center px-10 text-lg"
            >
              <span className="text-left font-semibold text-slate-700">
                {row.label}
              </span>
              <span className="text-center font-semibold text-slate-700">
                {row.rate || ''}
              </span>
              <span className="text-right text-xl font-semibold text-slate-800">
                {row.amount}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={exportStatement}
            className="flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-600 cursor-pointer"
          >
            <ExportOutlined />
            <span>导出图片</span>
          </button>
        </div>
      </section>
    </div>
  );
}
